import React, { useState, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { useApp } from '../context/AppContext';
import { api } from '../services/api';
import { Card, CardHeader } from '../components/UI/Card';
import Button from '../components/UI/Button';
import Badge from '../components/UI/Badge';
import { Select } from '../components/UI/Input';
import ConfirmModal from '../components/UI/ConfirmModal';
import GenieTip from '../components/GenieTip';
import './Import.css';

const fieldOptions = [
  { value: '', label: '— Ignorer —' },
  { value: 'gender', label: 'Civilité (M./Mme/Autre)' },
  { value: 'first_name', label: 'Prénom' },
  { value: 'last_name', label: 'Nom' },
  { value: 'email', label: 'E-mail' },
  { value: 'phone', label: 'Téléphone' },
  { value: 'birth_date', label: 'Date de naissance' },
  { value: 'address', label: 'Adresse' },
  { value: 'postal_code', label: 'NPA' },
  { value: 'city', label: 'Ville' },
  { value: 'region', label: 'Région' },
  { value: 'canton', label: 'Canton' },
  { value: 'category', label: 'Catégorie' },
  { value: 'relationship_memory', label: 'Mémoire relationnelle' },
  { value: 'relationship_memory', label: 'Remarques / Notes' },
  { value: 'tone', label: 'Ton relationnel' },
];

// Normalise une valeur brute vers monsieur/madame/autre/non_renseigne
function normalizeGender(val) {
  const v = String(val || '').trim().toLowerCase().replace(/\.$/, '');
  if (['m', 'mr', 'monsieur', 'homme', 'man', 'male', 'h', 'dr', 'prof', 'docteur', 'professeur'].includes(v)) return 'monsieur';
  if (['mme', 'mrs', 'madame', 'femme', 'woman', 'female', 'f', 'mademoiselle', 'mlle'].includes(v)) return 'madame';
  if (['autre', 'other', 'divers', 'non-binaire', 'nonbinaire', 'x'].includes(v)) return 'autre';
  return 'non_renseigne';
}

// Miroir exact du PHP AUTO_DETECT (map_headers)
const AUTO_DETECT = {
  // Civilité / Genre
  'civility': 'gender', 'civilite': 'gender', 'civilité': 'gender',
  'titre': 'gender', 'title': 'gender', 'salutation': 'gender',
  'mr/mme': 'gender', 'madame/monsieur': 'gender', 'genre': 'gender', 'gender': 'gender',
  'sexe': 'gender', 'sex': 'gender',
  // Prénom
  'first_name': 'first_name', 'prénom': 'first_name', 'prenom': 'first_name',
  'firstname': 'first_name', 'first name': 'first_name', 'given name': 'first_name', 'given_name': 'first_name',
  // Nom
  'last_name': 'last_name', 'nom': 'last_name', 'lastname': 'last_name',
  'last name': 'last_name', 'surname': 'last_name', 'family name': 'last_name', 'family_name': 'last_name', 'name': 'last_name',
  // E-mail
  'email': 'email', 'e-mail': 'email', 'e_mail': 'email', 'courriel': 'email', 'mail': 'email',
  // Téléphone
  'téléphone': 'phone', 'telephone': 'phone', 'phone': 'phone', 'phone_number': 'phone', 'phone number': 'phone',
  'mobile': 'phone', 'tél': 'phone', 'tel': 'phone', 'portable': 'phone', 'gsm': 'phone', 'cell': 'phone',
  // Date de naissance
  'birth_date': 'birth_date', 'birthdate': 'birth_date', 'birth date': 'birth_date',
  'date de naissance': 'birth_date', 'date_naissance': 'birth_date', 'naissance': 'birth_date',
  'birthday': 'birth_date', 'anniversaire': 'birth_date', 'dob': 'birth_date', 'date of birth': 'birth_date', 'date_of_birth': 'birth_date',
  // Adresse
  'adresse': 'address', 'address': 'address', 'rue': 'address', 'street': 'address', 'street address': 'address', 'street_address': 'address',
  // NPA / Code postal
  'postal_code': 'postal_code', 'npa': 'postal_code', 'code postal': 'postal_code', 'code_postal': 'postal_code',
  'postal code': 'postal_code', 'zip': 'postal_code', 'zipcode': 'postal_code', 'zip_code': 'postal_code',
  'zip code': 'postal_code', 'postcode': 'postal_code', 'cp': 'postal_code',
  // Ville
  'ville': 'city', 'city': 'city', 'localité': 'city', 'localite': 'city', 'town': 'city', 'municipality': 'city',
  // Canton / Région
  'canton': 'canton',
  'region': 'region', 'région': 'region', 'secteur': 'region', 'zone': 'region', 'district': 'region', 'state': 'region', 'province': 'region',
  // Catégorie
  'catégorie': 'category', 'categorie': 'category', 'category': 'category', 'type': 'category', 'segment': 'category',
  // Notes
  'remarque': 'relationship_memory', 'remarques': 'relationship_memory', 'comment': 'relationship_memory', 'comments': 'relationship_memory',
  'note': 'relationship_memory', 'notes': 'relationship_memory', 'commentaire': 'relationship_memory', 'commentaires': 'relationship_memory', 'observations': 'relationship_memory',
};

// Normalize birth_date to YYYY-MM-DD regardless of input format (DD.MM.YYYY, DD/MM/YYYY, Excel serial, ISO)
function normalizeBirthDate(val) {
  if (!val) return null;
  // Excel serial number
  if (typeof val === 'number') {
    const d = XLSX.SSF.parse_date_code(val);
    if (d) return `${d.y}-${String(d.m).padStart(2,'0')}-${String(d.d).padStart(2,'0')}`;
  }
  const s = String(val).trim();
  // DD.MM.YYYY or DD/MM/YYYY
  const dmy = s.match(/^(\d{1,2})[./](\d{1,2})[./](\d{4})$/);
  if (dmy) return `${dmy[3]}-${dmy[2].padStart(2,'0')}-${dmy[1].padStart(2,'0')}`;
  // YYYY-MM-DD already
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  return null;
}


// ── Parseur vCard (.vcf) — exports Contacts iPhone / Android / Outlook ──────
// v3.0/v4.0 : lignes pliées (continuation par espace), N:Nom;Prénom, TEL, EMAIL,
// BDAY (AAAAMMJJ ou AAAA-MM-JJ), ADR (;;rue;ville;;NPA;pays). On produit des
// colonnes FR standard → l'auto-détection existante fait le reste.
function parseVcf(text) {
  const unfolded = text.replace(/\r/g, '').replace(/\n[ \t]/g, '');
  const cards = unfolded.split(/BEGIN:VCARD/i).slice(1);
  const heads = ['Prénom', 'Nom', 'E-mail', 'Téléphone', 'Date de naissance', 'Adresse', 'NPA', 'Ville'];
  const rows = [];
  const val = (line) => (line.split(':').slice(1).join(':') || '').trim();
  for (const card of cards) {
    const lines = card.split('\n');
    let first = '', last = '', email = '', phone = '', bday = '', street = '', npa = '', city = '', fn = '';
    for (const line of lines) {
      const L = line.trim();
      const U = L.toUpperCase();
      if (U.startsWith('N:') || U.startsWith('N;')) {
        const parts = val(L).split(';');
        last = last || (parts[0] || '').trim();
        first = first || (parts[1] || '').trim();
      } else if (U.startsWith('FN')) {
        fn = fn || val(L);
      } else if (U.startsWith('EMAIL')) {
        email = email || val(L);
      } else if (U.startsWith('TEL')) {
        phone = phone || val(L);
      } else if (U.startsWith('BDAY')) {
        let b = val(L).replace(/[^0-9-]/g, '');
        if (/^\d{8}$/.test(b)) b = `${b.slice(0, 4)}-${b.slice(4, 6)}-${b.slice(6, 8)}`;
        if (/^\d{4}-\d{2}-\d{2}$/.test(b) && !b.startsWith('1604')) bday = bday || b; // 1604 = « année inconnue » Apple
      } else if (U.startsWith('ADR')) {
        const parts = val(L).split(';');
        street = street || (parts[2] || '').trim();
        city = city || (parts[3] || '').trim();
        npa = npa || (parts[5] || '').trim();
      }
    }
    if (!first && !last && fn) {
      const parts = fn.trim().split(/\s+/);
      first = parts[0] || '';
      last = parts.slice(1).join(' ');
    }
    if (first || last || email || phone) rows.push([first, last, email, phone, bday, street, npa, city]);
  }
  return { heads, rows };
}

// Décode un fichier texte en gérant les encodages hérités : UTF-8 (avec BOM),
// Mac Roman (vieux exports Mac — octet 0x8E = é) et Windows-1252 / Latin-1.
// Sans ça, un fichier suisse de 2017 s'affiche « Pr�nom », « T�l�phone »…
function decodeText(buffer) {
  const bytes = new Uint8Array(buffer);
  if (bytes[0] === 0xEF && bytes[1] === 0xBB && bytes[2] === 0xBF) {
    return new TextDecoder('utf-8').decode(bytes.subarray(3)); // BOM UTF-8
  }
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  } catch {
    let macish = 0;
    for (let i = 0; i < bytes.length; i++) if (bytes[i] >= 0x80 && bytes[i] <= 0x9F) macish++;
    // Beaucoup d'octets 0x80–0x9F → Mac Roman ; sinon Windows-1252.
    const enc = macish > 0 ? 'macintosh' : 'windows-1252';
    try { return new TextDecoder(enc).decode(bytes); }
    catch { return new TextDecoder('windows-1252').decode(bytes); }
  }
}

export default function Import() {
  const { contacts, addNotification, refreshContacts } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = location.state?.returnTo || null;
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [headers, setHeaders] = useState([]);
  const [preview, setPreview] = useState([]);
  const [allRows, setAllRows] = useState([]);
  const [mapping, setMapping] = useState({});
  const [mode, setMode] = useState('add_update');
  const [consentConfirmed, setConsentConfirmed] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [confirmReplace, setConfirmReplace] = useState(false);
  const inputRef = useRef();

  // ── Gender breakdown from mapped civility column ──────────────────────────
  const genderBreakdown = useMemo(() => {
    const civilityColIdx = Object.entries(mapping).find(([, v]) => v === 'gender')?.[0];
    if (civilityColIdx === undefined || !allRows.length) return null;
    const idx = Number(civilityColIdx);
    const counts = { monsieur: 0, madame: 0, autre: 0, non_renseigne: 0 };
    allRows.forEach(row => { counts[normalizeGender(row[idx])]++; });
    return counts;
  }, [mapping, allRows]);

  const processRows = (heads, rows) => {
    setHeaders(heads);
    setAllRows(rows);
    setPreview(rows.slice(0, 5));
    const autoMap = {};
    // Premier passage : mapper tout
    heads.forEach((h, i) => {
      const key = h.toLowerCase().trim();
      if (AUTO_DETECT[key]) autoMap[i] = AUTO_DETECT[key];
    });
    // Résolution des conflits : si plusieurs colonnes mappent sur le même champ,
    // garder celle dont le nom de colonne est le plus explicite (priorité : civility > gender > autres)
    const GENDER_PRIORITY = ['civility', 'civilite', 'civilité', 'salutation', 'titre', 'gender', 'genre', 'sexe'];
    const genderCols = heads
      .map((h, i) => ({ i, key: h.toLowerCase().trim() }))
      .filter(({ key }) => AUTO_DETECT[key] === 'gender');
    if (genderCols.length > 1) {
      // Garder uniquement le plus prioritaire
      const best = genderCols.sort((a, b) =>
        (GENDER_PRIORITY.indexOf(a.key) === -1 ? 99 : GENDER_PRIORITY.indexOf(a.key)) -
        (GENDER_PRIORITY.indexOf(b.key) === -1 ? 99 : GENDER_PRIORITY.indexOf(b.key))
      )[0];
      genderCols.forEach(({ i }) => { if (i !== best.i) delete autoMap[i]; });
    }
    // Résolution générale : un même champ mappé sur PLUSIEURS colonnes → garder
    // celle qui a des DONNÉES (une colonne « Email » vide ne doit pas voler le
    // champ à une colonne « Mail » remplie). À défaut, garder la première.
    const colHasData = (ci) => rows.some(r => String(r?.[ci] ?? '').trim() !== '');
    const byField = {};
    Object.entries(autoMap).forEach(([col, field]) => {
      (byField[field] = byField[field] || []).push(Number(col));
    });
    Object.values(byField).forEach(cols => {
      if (cols.length < 2) return;
      const withData = cols.filter(colHasData);
      const keep = (withData.length ? withData : cols)[0];
      cols.forEach(c => { if (c !== keep) delete autoMap[c]; });
    });
    setMapping(autoMap);
    setStep(2);
  };

  const handleFile = (f) => {
    if (!f) return;
    setFile(f);
    const isExcel = /\.(xlsx|xls)$/i.test(f.name);
    const isVcf   = /\.vcf$/i.test(f.name);

    if (isVcf) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const { heads, rows } = parseVcf(e.target.result);
        if (!rows.length) { addNotification('Aucun contact lisible dans ce fichier .vcf.', 'error'); return; }
        processRows(heads, rows);
      };
      reader.readAsText(f, 'UTF-8');
    } else if (isExcel) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const wb = XLSX.read(e.target.result, { type: 'array', cellDates: false });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const data = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
        if (!data.length) return;
        const heads = data[0].map(h => String(h).trim());
        const rows = data.slice(1).filter(r => r.some(c => c !== '')).map(r =>
          heads.map((_, i) => r[i] !== undefined ? r[i] : '')
        );
        processRows(heads, rows);
      };
      reader.readAsArrayBuffer(f);
    } else {
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = decodeText(e.target.result);
        const lines = text.split(/\r\n|\r|\n/).filter(l => l.trim());
        if (!lines.length) return;
        const sep = lines[0].includes(';') ? ';' : ',';
        const heads = lines[0].split(sep).map(h => h.trim().replace(/^"|"$/g, ''));
        const rows = lines.slice(1).map(l => l.split(sep).map(v => v.trim().replace(/^"|"$/g, '')));
        processRows(heads, rows);
      };
      reader.readAsArrayBuffer(f);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files[0];
    if (f) handleFile(f);
  };

  const handleImport = async () => {
    setImporting(true);
    try {
      // Send file + mapping + mode to the backend in a single batch request
      const formData = new FormData();
      formData.append('file', file);
      // Send mapping as indexed fields so Laravel parses it as array: mapping[0]=first_name, mapping[2]=email…
      Object.entries(mapping).forEach(([colIdx, field]) => {
        if (field) formData.append(`mapping[${colIdx}]`, field);
      });
      formData.append('mode', mode);
      formData.append('consent_confirmed', consentConfirmed ? '1' : '0');

      const res = await api.processImport(formData);

      const added   = res.added_count   ?? 0;
      const updated = res.updated_count ?? 0;
      const skipped = res.skipped_count ?? 0;
      const total   = res.total_rows    ?? (added + updated + skipped);

      setResult({ added, updated, skipped, total });
      setStep(4);
      if (returnTo) {
        // Vient de l'onboarding → on y retourne après avoir montré le résultat
        setTimeout(() => navigate(returnTo), 2600);
      }
      addNotification(`✅ ${added} contacts ajoutés, ${updated} mis à jour !`);

      // Refresh local contacts list
      await refreshContacts();
    } catch (err) {
      addNotification(`❌ Erreur import : ${err.message}`, 'error');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="import-page">
      {/* ── Import Hero ── */}
      <div className="import-hero">
        <div className="import-hero-nebula" />
        <div className="import-hero-inner">
          <div className="import-hero-left">
            <div className="import-hero-icon-wrap">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="8 17 12 21 16 17"/>
                <line x1="12" y1="12" x2="12" y2="21"/>
                <path d="M20.88 18.09A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.72"/>
              </svg>
            </div>
            <div>
              <h1 className="import-hero-title">Importer des contacts</h1>
              <p className="import-hero-sub">Appee détecte automatiquement tes colonnes et nettoie les doublons</p>
            </div>
          </div>
          <div className="import-hero-chips">
            {['XLSX', 'XLS', 'CSV', '10 champs détectés', '🔒 RGPD OK'].map(c => (
              <span key={c} className="import-hero-chip">{c}</span>
            ))}
          </div>
        </div>
      </div>

      <GenieTip
        id="import"
        title="📥 Importer tes contacts"
        foot={<>Tes données restent chez toi — conformité RGPD intégrée.</>}
      >
        Glisse ton fichier Excel ou CSV : Appee détecte les colonnes, enlève les doublons et range tout. Rien à formater à la main.
      </GenieTip>

      {/* Steps */}
      <div className="import-steps">
        {['Fichier', 'Mapping', 'Options', 'Résultat'].map((s, i) => (
          <div key={i} className={`import-step ${step > i+1 ? 'done' : step === i+1 ? 'active' : ''}`}>
            <div className="import-step-dot">{step > i+1 ? '✓' : i+1}</div>
            <span>{s}</span>
          </div>
        ))}
      </div>

      {/* Step 1: Upload */}
      {step === 1 && (
        <Card>
          <div
            className="drop-zone"
            onDrop={handleDrop}
            onDragOver={e => e.preventDefault()}
            onClick={() => inputRef.current.click()}
          >
            <div className="drop-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="16 16 12 12 8 16"/>
                <line x1="12" y1="12" x2="12" y2="21"/>
                <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3"/>
              </svg>
            </div>
            <div className="drop-title">Glissez votre fichier ici</div>
            <div className="drop-or">ou</div>
            <div className="drop-sub" style={{ fontWeight: 600, color: 'var(--violet)' }}>Cliquer pour sélectionner</div>
            <div className="drop-formats" style={{ marginTop: 10 }}>
              <Badge variant="violet">XLSX</Badge>
              <Badge variant="violet">XLS</Badge>
              <Badge variant="violet">CSV</Badge>
              <Badge variant="violet">VCF 📱</Badge>
            </div>
            <input ref={inputRef} type="file" accept=".csv,.xlsx,.xls,.vcf" style={{ display: 'none' }} onChange={e => handleFile(e.target.files[0])} />
          </div>

          <div className="import-tips">
            <h4>Colonnes reconnues automatiquement :</h4>
            <div className="import-tips-list">
              {['Prénom', 'Nom', 'E-mail', 'Téléphone', 'Date naissance', 'Adresse', 'NPA', 'Ville', 'Canton', 'Catégorie'].map(t => (
                <span key={t} className="import-tip-badge">✓ {t}</span>
              ))}
            </div>
            <button
              type="button"
              onClick={() => {
                // Gabarit CSV prêt à remplir — séparateur ; (Excel suisse/FR l'ouvre en colonnes)
                const csv = 'Civilité;Prénom;Nom;E-mail;Téléphone;Date de naissance;Adresse;NPA;Ville;Catégorie\n'
                  + 'Monsieur;Jean;Exemple;jean@exemple.ch;+41 79 000 00 00;14.05.1975;Rue des Alpes 12;1700;Fribourg;Client fidèle\n'
                  + 'Madame;Marie;Modèle;marie@exemple.ch;+41 78 000 00 00;02.11.1988;Grand-Rue 3;1630;Bulle;';
                const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' }); // BOM → accents OK dans Excel
                const a = document.createElement('a');
                a.href = URL.createObjectURL(blob);
                a.download = 'modele-contacts-appee.csv';
                a.click();
                URL.revokeObjectURL(a.href);
              }}
              style={{ marginTop: 10, border: '1.5px solid var(--line)', background: '#fff', color: 'var(--violet)',
                fontFamily: 'inherit', fontWeight: 700, fontSize: 12.5, borderRadius: 999, padding: '8px 14px', cursor: 'pointer' }}
            >
              📄 Télécharger un fichier modèle (à ouvrir dans Excel)
            </button>
          </div>
        </Card>
      )}

      {/* Step 2: Mapping */}
      {step === 2 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <Card>
            <CardHeader title="🗂 Correspondance des colonnes" subtitle={`Fichier : ${file?.name}`} />
            {(() => {
              // Colonnes du fichier qui ne seront PAS importées — dit clairement,
              // au lieu de les ignorer en silence (friction novice)
              const unmapped = headers.map((h, i) => (!mapping[i] ? h : null)).filter(Boolean);
              if (!unmapped.length) return null;
              return (
                <div style={{ background: '#FFF7ED', border: '1px solid #FED7AA', borderRadius: 10, padding: '10px 14px', marginBottom: 12, fontSize: 12.5, color: '#78350F' }}>
                  👀 {unmapped.length === 1 ? 'Cette colonne de ton fichier ne sera pas importée' : `Ces ${unmapped.length} colonnes de ton fichier ne seront pas importées`} :{' '}
                  <b>{unmapped.slice(0, 4).join(', ')}{unmapped.length > 4 ? ` et ${unmapped.length - 4} autres` : ''}</b>.
                  {' '}Si l'une d'elles compte pour toi, choisis un « Champ Appee » sur sa ligne ci-dessous.
                </div>
              );
            })()}
            <div className="mapping-table">
              <div className="mapping-header">
                <div>Colonne du fichier</div>
                <div>Exemples</div>
                <div>Champ Appee</div>
              </div>
              {headers.map((h, i) => {
                // Masque les colonnes de padding fantômes (aucun en-tête ET aucune
                // donnée) — les exports tableur en trainent souvent des centaines.
                const hasHeader = String(h || '').trim() !== '';
                const hasData   = preview.some(row => String(row?.[i] ?? '').trim() !== '');
                if (!hasHeader && !hasData) return null;
                // Un champ = une seule colonne. On masque les champs déjà pris par
                // d'AUTRES colonnes ; on garde toujours « Ignorer » et la valeur
                // actuelle de CETTE colonne (sinon le menu s'afficherait vide).
                const usedByOthers = new Set(
                  Object.entries(mapping)
                    .filter(([col, val]) => Number(col) !== i && val)
                    .map(([, val]) => val)
                );
                const availableOptions = fieldOptions.filter(
                  o => !o.value || o.value === mapping[i] || !usedByOthers.has(o.value)
                );
                return (
                  <div key={i} className="mapping-row">
                    <div className="mapping-col-name">{hasHeader ? h : <em style={{ color: 'var(--muted-2)' }}>Colonne {i + 1}</em>}</div>
                    <div className="mapping-examples">
                      {preview.slice(0, 2).map((row, ri) => (
                        <span key={ri} className="mapping-example">{row[i] || '—'}</span>
                      ))}
                    </div>
                    <Select
                      value={mapping[i] || ''}
                      onChange={e => setMapping(m => ({ ...m, [i]: e.target.value }))}
                    >
                      {availableOptions.map((o, oi) => <option key={oi} value={o.value}>{o.label}</option>)}
                    </Select>
                  </div>
                );
              })}
            </div>
            {/* Appee gender breakdown callout */}
            {genderBreakdown && (
              <div style={{ marginTop: 16, background: '#F5F3FF', border: '1px solid #DDD6FE', borderRadius: 10, padding: '12px 16px' }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: '#4C1D95', marginBottom: 6 }}>
                  💡 J'ai analysé la colonne civilité de ton fichier :
                </div>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: 13 }}>
                  {genderBreakdown.madame > 0    && <span style={{ color: '#DB2777', fontWeight: 600 }}>👩 {genderBreakdown.madame} femmes</span>}
                  {genderBreakdown.monsieur > 0  && <span style={{ color: '#2563EB', fontWeight: 600 }}>👨 {genderBreakdown.monsieur} hommes</span>}
                  {genderBreakdown.autre > 0     && <span style={{ color: '#7C3AED', fontWeight: 600 }}>✦ {genderBreakdown.autre} autre</span>}
                  {genderBreakdown.non_renseigne > 0 && <span style={{ color: '#9CA3AF', fontWeight: 600 }}>◌ {genderBreakdown.non_renseigne} sans civilité</span>}
                </div>
                {genderBreakdown.non_renseigne > 0 && (
                  <div style={{ fontSize: 12, color: '#6B7280', marginTop: 6 }}>
                    Tu pourras compléter les fiches sans civilité plus tard pour améliorer la qualité de ta base.
                  </div>
                )}
              </div>
            )}

            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
              <Button variant="ghost" onClick={() => setStep(1)}>← Retour</Button>
              <Button onClick={() => setStep(3)}>Confirmer le mapping →</Button>
            </div>
          </Card>

          {/* Preview */}
          <Card>
            <CardHeader title="👁 Aperçu des données" subtitle="5 premières lignes" />
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr>{headers.map((h, i) => <th key={i} style={{ padding: '8px 12px', textAlign: 'left', background: 'var(--bg)', fontWeight: 700, borderBottom: '1px solid var(--line)' }}>{h}</th>)}</tr>
                </thead>
                <tbody>
                  {preview.map((row, ri) => (
                    <tr key={ri}>
                      {row.map((cell, ci) => <td key={ci} style={{ padding: '8px 12px', borderBottom: '1px solid var(--line)', color: 'var(--muted)' }}>{cell}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}

      {/* Step 3: Options */}
      {step === 3 && (
        <Card>
          <CardHeader title="⚙️ Options d'import" />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {[
              { value: 'add_only', label: 'Ajouter uniquement', sub: "N'importe que les nouveaux contacts" },
              { value: 'update_only', label: 'Mettre à jour uniquement', sub: "Met à jour les contacts existants (par e-mail)" },
              { value: 'add_update', label: 'Ajouter et mettre à jour', sub: 'Ajoute les nouveaux et met à jour les existants (recommandé)' },
              { value: 'replace', label: 'Remplacer la base', sub: '⚠️ Supprime tous les contacts existants avant d\'importer' },
            ].map(o => (
              <button
                key={o.value}
                className={`target-option ${mode === o.value ? 'selected' : ''} ${o.value === 'replace' ? 'dangerous' : ''}`}
                onClick={() => setMode(o.value)}
              >
                <div className="target-radio">{mode === o.value ? '●' : '○'}</div>
                <div>
                  <div className="target-label">{o.label}</div>
                  <div className="target-sub" style={{ color: o.value === 'replace' ? 'var(--danger)' : '' }}>{o.sub}</div>
                </div>
              </button>
            ))}
          </div>

          {/* Consentement (nLPD/RGPD) : attestation horodatée côté serveur */}
          <label
            style={{
              display: 'flex', alignItems: 'flex-start', gap: 10, marginTop: 18,
              padding: '12px 14px', borderRadius: 10, cursor: 'pointer',
              background: consentConfirmed ? 'rgba(16,163,107,0.08)' : 'rgba(90,53,255,0.05)',
              border: `1px solid ${consentConfirmed ? 'rgba(16,163,107,0.35)' : 'rgba(90,53,255,0.15)'}`,
            }}
          >
            <input
              type="checkbox"
              checked={consentConfirmed}
              onChange={e => setConsentConfirmed(e.target.checked)}
              style={{ marginTop: 3 }}
            />
            <div>
              <div style={{ fontWeight: 600, fontSize: 14 }}>
                ✅ Ces contacts m'ont donné leur accord pour recevoir mes communications
              </div>
              <div style={{ fontSize: 12.5, color: 'var(--text-secondary, #6B7280)', marginTop: 2 }}>
                Enregistre le consentement (horodaté) pour chaque contact importé — requis par la loi suisse
                sur la protection des données (nLPD). Si ton fichier contient déjà une colonne consentement,
                c'est elle qui fait foi.
              </div>
            </div>
          </label>

          <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
            <Button variant="ghost" onClick={() => setStep(2)}>← Retour</Button>
            <Button loading={importing} onClick={() => {
              // Mode "remplacer" = destructif → on confirme avant
              if (mode === 'replace') { setConfirmReplace(true); return; }
              handleImport();
            }}>
              🚀 Lancer l'import
            </Button>
          </div>
        </Card>
      )}

      {/* Confirmation avant remplacement total de la base (destructif) */}
      <ConfirmModal
        open={confirmReplace}
        title="Remplacer toute ta base ? ⚠️"
        message={`Cette option va d'abord SUPPRIMER tes ${contacts.length} contact${contacts.length > 1 ? 's' : ''} actuel${contacts.length > 1 ? 's' : ''}, puis importer le nouveau fichier. C'est définitif. Si tu veux juste ajouter, reviens en arrière et choisis « Ajouter et mettre à jour ».`}
        confirmLabel="Oui, tout remplacer"
        cancelLabel="Non, annuler"
        danger
        onConfirm={() => { setConfirmReplace(false); handleImport(); }}
        onCancel={() => setConfirmReplace(false)}
      />

      {/* Step 4: Result */}
      {step === 4 && result && (
        <Card>
          <div className="import-result">
            <div className="import-result-hero">
              <div className="import-result-emoji">🎉</div>
              <h3 className="import-result-title">Import réussi !</h3>
              <p className="import-result-sub">
                {genderBreakdown && (genderBreakdown.madame + genderBreakdown.monsieur) > 0
                  ? `${result.added + result.updated} contacts importés — dont ${genderBreakdown.madame} femmes et ${genderBreakdown.monsieur} hommes.`
                  : `${result.added + result.updated} contacts importés avec succès.`}
              </p>
            </div>

            <div className="import-result-stats">
              <div className="result-stat">
                <div className="result-stat-val" style={{ color: '#10B981' }}>{result.added}</div>
                <div className="result-stat-label">Ajoutés</div>
              </div>
              <div className="result-stat">
                <div className="result-stat-val" style={{ color: '#5A35FF' }}>{result.updated}</div>
                <div className="result-stat-label">Mis à jour</div>
              </div>
              <div className="result-stat">
                <div className="result-stat-val" style={{ color: '#9CA3AF' }}>{result.skipped}</div>
                <div className="result-stat-label">Ignorés</div>
              </div>
              <div className="result-stat">
                <div className="result-stat-val">{result.total}</div>
                <div className="result-stat-label">Total</div>
              </div>
            </div>

            <div className="import-result-next">
              <div className="import-result-next-title">Et maintenant ?</div>
            </div>
            <div className="import-result-actions">
              <Button onClick={() => navigate('/contacts')}>Voir mes contacts</Button>
              <Button variant="secondary" onClick={() => { setStep(1); setFile(null); setResult(null); }}>Nouvel import</Button>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
